import type {
  SectionFrontSection,
  SectionFrontSecurity,
  TodayInMarketsCategory,
  TodayInMarketsItem,
} from "~types/types";

import { type TranslatedMessageProps } from "~utils/i18n/translate";

type MarketCategoryEntry = {
  id: string;
  label: TranslatedMessageProps;
};

// exclude tickers which have all reference data but no sparkline
const EXCLUDED_IDS = new Set<string>(["LB1:IND", "RBATCTR:IND"]);

const MARKET_CATEGORY_LABELS = {
  stocks: {
    id: "indices",
    label: { id: "popularIndices", defaultMessage: "Popular Indices" },
  },
  "rates-bonds": {
    id: "ratesAndBonds",
    label: {
      id: "popularRatesAndBonds",
      defaultMessage: "Popular Rates & Bonds",
    },
  },
  currencies: {
    id: "currencies",
    label: { id: "popularCurrencies", defaultMessage: "Popular Currencies" },
  },
  commodities: {
    id: "commodities",
    label: { id: "popularCommodities", defaultMessage: "Popular Commodities" },
  },
  topPerformers: {
    id: "topPerformers",
    label: { id: "topPerformers", defaultMessage: "Top Performers" },
  },
  underPerformers: {
    id: "underPerformers",
    label: { id: "underPerformers", defaultMessage: "Under Performers" },
  },
} as const satisfies Record<string, MarketCategoryEntry>;

export type MarketCategoryKey = keyof typeof MARKET_CATEGORY_LABELS;

function getCategoryTabLabel(
  sectionFrontName: MarketCategoryKey
): MarketCategoryEntry {
  // Returns a pre-defined label based on the section front name.
  // Returns default 'stocks' label if no matches are found.
  // Due to translations every label must be pre-defined.
  return (
    MARKET_CATEGORY_LABELS[sectionFrontName] ?? MARKET_CATEGORY_LABELS.stocks
  );
}

export function deriveTodayInMarketsTabs(
  sectionFrontData: SectionFrontSection[],
  sectionFrontName: MarketCategoryKey
): TodayInMarketsCategory[] {
  const allSecurities = sectionFrontData.flatMap(
    (section) => section.securities || []
  );

  const baseItems: TodayInMarketsItem[] = allSecurities
    .filter((security: SectionFrontSecurity) => {
      if (EXCLUDED_IDS.has(security.id)) return false;

      // futures which don't have highPrice and lowPrice, don't seem to have sparkline
      // Filter these futures out E.g PP1:IND, SD1:IND, SCT1:IND
      return (
        security.highPrice !== undefined || security.lowPrice !== undefined
      );
    })
    .map((security: SectionFrontSecurity) => ({
      id: security.id,
      currency: security.issuedCurrency ?? "USD",
      percentChange: security.percentChange1Day,
      priceChange1Day: security.priceChange1Day,
      price: security.price,
      priceSet: [],
      priceMovements: [],
      yesterdayPrice: security.previousClosingPriceOneTradingDayAgo,
      volume: security.volume,
      highPrice: security.highPrice,
      lowPrice: security.lowPrice,
      previousClosingPriceOneTradingDayAgo:
        security.previousClosingPriceOneTradingDayAgo,
      volatility:
        (security.highPrice - security.lowPrice) /
        security.previousClosingPriceOneTradingDayAgo,
    }));

  const topGainers = baseItems
    .sort((a, b) => b.percentChange - a.percentChange)
    .slice(0, 10);

  const topLosers = baseItems
    .sort((a, b) => a.percentChange - b.percentChange)
    .slice(0, 10);

  const popular = baseItems.sort((a, b) => b.volume - a.volume).slice(0, 10);
  const popularCategory = getCategoryTabLabel(sectionFrontName);

  return [
    {
      ...popularCategory,
      dataItems: popular,
    },
    {
      ...MARKET_CATEGORY_LABELS.topPerformers,
      dataItems: topGainers,
    },
    {
      ...MARKET_CATEGORY_LABELS.underPerformers,
      dataItems: topLosers,
    },
  ];
}
